import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import Footer from './Footer';
import { useScrollAnimation } from '../../hooks/useScrollAnimation';
import { setByPath } from '../../utils/objectPath';
import Icon from '../services/editable/commonIcon';
import EditableImage from '../services/editable/EditableImage';
import { buildMobileStyleSheet } from '../services/editable/styleUtils';
import { useEditablePage } from '../services/editable/useEditablePage';
import CaseStudiesEditor from '../../admin/editors/CaseStudiesEditor';
import TestimonialsEditor from '../../admin/editors/TestimonialsEditor';
import { uploadVideoToCloudinary } from '../../utils/cloudinaryUpload';
import arrowIcon from '../../assets/rightarrow.svg';
import './HeroSection.css';
import './SolutionNeedSection.css';
import './NumbersStatsSection.css';
import './AISolutionsSection.css';
import './AIAutomationSection.css';
import './CaseStudiesSection.css';
import './WhyAISection.css';
import './HowDatabiqsSection.css';
import './TestimonialsSection.css';
import './MeetingBannerSection.css';
import '../services/editable/editable.css';

/**
 * Generic, CMS-driven Home page editor — the Home-page counterpart to
 * EditableAboutPage / TemplateServicePage. Admin-only: the live `/` route
 * keeps using Home.js's own section components (HeroSection.js,
 * SolutionNeedSection.js, …) unchanged, now reading their text/array content
 * from the same `homePage` CMS data this editor writes to — so what's edited
 * here is exactly what ships.
 *
 * This view intentionally simplifies the most fragile production behavior
 * instead of re-implementing it — none of it is rendered to real visitors:
 *  - Hero: a fully editable "Get a Quote" card (text/link) replaces the globe.
 *  - Solution-need: the scroll-pinned/auto-advancing/video-primed accordion
 *    becomes a plain stacked list of the 4 options, each with its video URL
 *    editable right here too.
 *  - AI Solutions / AI Automation: the self-looping Lottie is skipped; the
 *    Cloudinary diagram image is shown via EditableImage instead.
 *  - Case studies / Testimonials: the real CaseStudiesEditor/TestimonialsEditor
 *    admin panels (full CRUD, same ones used elsewhere) are embedded inline
 *    here so every card/testimonial is editable on this one page — nothing
 *    is only "editable elsewhere".
 *
 * `fullContent`/`onFullContentChange` carry the WHOLE site content (not just
 * `homePage`) so the embedded case-studies/testimonials/media editors above
 * can read and write their own CMS sections through the same auto-save path
 * as the rest of this page (see HomePageEditor.jsx).
 */

/** Paste a Cloudinary URL, or upload a video file directly to Cloudinary (unsigned preset — see cloudinaryUpload.js). */
function VideoUrlField({ value, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [error, setError] = useState('');
  const fileInputRef = useRef(null);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setUploading(true);
    setError('');
    setProgress(0);
    try {
      const url = await uploadVideoToCloudinary(file, { onProgress: setProgress });
      onChange(url);
    } catch (err) {
      setError(err?.message || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="svcpg-popover__field" style={{ maxWidth: 420, margin: '0 0 10px', gap: 6 }}>
      <span>Video clip</span>
      <input
        type="text"
        value={value || ''}
        placeholder="https://res.cloudinary.com/…"
        onChange={(e) => onChange(e.target.value)}
      />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 2 }}>
        <button
          type="button"
          className="svcpg-upload-btn"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? `Uploading… ${progress}%` : 'Upload video'}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="video/mp4,video/webm,video/quicktime"
          style={{ display: 'none' }}
          onChange={handleFile}
        />
      </div>
      {error ? <span className="svcpg-popover__error">{error}</span> : null}
    </div>
  );
}

export default function EditableHomePage({
  data,
  styles,
  editable = false,
  onChange,
  preview = false,
  fullContent,
  onFullContentChange,
}) {
  const isEditing = editable && !preview;

  const heroRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const solutionNeedRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const numbersStatsRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const aiSolutionsRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const aiAutomationRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const caseStudiesRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const whyAIRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const howDatabiqsRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const testimonialsRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });
  const finalCtaRef = useScrollAnimation({ threshold: 0.1, immediate: isEditing });

  const {
    T,
    RemoveBtn,
    AddBtn,
    SectionToggle,
    showSection,
    isSectionHidden,
    IconPick,
    LinkPick,
  } = useEditablePage({ data, styles, onChange, isEditing });

  return (
    <div className="home">
      <style>{buildMobileStyleSheet(styles, 640)}</style>

      {/* ============ Hero ============ */}
      {showSection('hero') ? (
        <section
          className="hero-section hero-section--globe-ready"
          ref={heroRef}
          data-svcpg-hidden={isEditing && isSectionHidden('hero') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="hero" label="Hero" />
          <div className="hero-ambient-glow" aria-hidden="true" />
          <div className="hero-container">
            <div className="hero-content">
              <h1 className="hero-headline">
                {T('hero.titleLine1', 'hero.titleLine1', data.hero.titleLine1, 'span')}
                <br />
                <span className="headline-purple">{T('hero.titleAccent', 'hero.titleAccent', data.hero.titleAccent, 'span')}</span>
                <br />
                {T('hero.titleLine2', 'hero.titleLine2', data.hero.titleLine2, 'span')}
                <br />
                {T('hero.titleLine3', 'hero.titleLine3', data.hero.titleLine3, 'span')}
              </h1>
              <p className="hero-description">{T('hero.description', 'hero.description', data.hero.description, 'span')}</p>

              <Link className="hero-cta-contact" to={data.hero.ctaTo} aria-label="Contact us">
                {T('hero.ctaLabel', 'hero.ctaLabel', data.hero.ctaLabel, 'span')}
                {LinkPick('hero.ctaTo', data.hero.ctaTo, <Icon name="arrowRight" size={14} />, 'Hero CTA')}
              </Link>
            </div>

            <div className="hero-visual hero-visual--quote">
              <div className="hero-quote-card">
                {T('hero.quoteTag', 'hero.quoteTag', data.hero.quoteTag, 'span', 'hero-quote-card__tag')}
                {T('hero.quoteTitle', 'hero.quoteTitle', data.hero.quoteTitle, 'h3', 'hero-quote-card__title')}
                {T('hero.quoteDesc', 'hero.quoteDesc', data.hero.quoteDesc, 'p', 'hero-quote-card__desc')}
                <Link to={data.hero.quoteCtaTo} className="hero-quote-card__cta">
                  {T('hero.quoteCtaLabel', 'hero.quoteCtaLabel', data.hero.quoteCtaLabel, 'span')}
                  <span className="hero-quote-card__cta-icon">
                    {LinkPick('hero.quoteCtaTo', data.hero.quoteCtaTo, <Icon name="arrowRight" size={20} color="#fff" />, 'Quote CTA')}
                  </span>
                </Link>
                <ul className="hero-quote-card__trust">
                  {data.hero.quoteTrust.map((t, i) => (
                    <li key={i} className={isEditing ? 'svcpg-removable' : ''}>
                      {T(`hero.quoteTrust.${i}`, `hero.quoteTrust.${i}`, t, 'span')}
                      <RemoveBtn path="hero.quoteTrust" index={i} small label="Remove item" />
                    </li>
                  ))}
                </ul>
                <AddBtn path="hero.quoteTrust" label="Add trust item" template="New reassurance" />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ Solution need ============ */}
      {showSection('solutionNeed') ? (
        <section
          className="solution-need-section"
          ref={solutionNeedRef}
          data-svcpg-hidden={isEditing && isSectionHidden('solutionNeed') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="solutionNeed" label="What kind of solution" />
          <div className="section-container">
            <h2 className="solution-need__heading">
              {T('solutionNeed.headingPrefix', 'solutionNeed.headingPrefix', data.solutionNeed.headingPrefix, 'span')}{' '}
              <span className="headline-purple">
                {T('solutionNeed.headingAccent', 'solutionNeed.headingAccent', data.solutionNeed.headingAccent, 'span')}
              </span>{' '}
              {T('solutionNeed.headingSuffix', 'solutionNeed.headingSuffix', data.solutionNeed.headingSuffix, 'span')}
            </h2>
            <p className="solution-need__subheading">
              {T('solutionNeed.subheading', 'solutionNeed.subheading', data.solutionNeed.subheading, 'span')}
            </p>

            <div style={{ display: 'grid', gap: 18, marginTop: 24 }}>
              {data.solutionNeed.options.map((option, i) => (
                <article className={`solution-need__detail${isEditing ? ' svcpg-removable' : ''}`} key={option.id} style={{ position: 'relative' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
                    <span className="solution-need__tab-icon-wrap" style={{ position: 'static' }}>
                      {IconPick(`solutionNeed.options.${i}.icon`, `solutionNeed.options.${i}.iconName`, option.iconName, 20)}
                    </span>
                    {T(`solutionNeed.options.${i}.title`, `solutionNeed.options.${i}.title`, option.title, 'h3', 'solution-need__detail-title')}
                    <RemoveBtn path="solutionNeed.options" index={i} label="Remove option" />
                  </div>
                  {T(`solutionNeed.options.${i}.subheading`, `solutionNeed.options.${i}.subheading`, option.subheading, 'p', 'solution-need__detail-subheading')}
                  {isEditing ? (
                    <VideoUrlField
                      value={fullContent?.media?.solutionVideos?.[option.id]}
                      onChange={(url) =>
                        onFullContentChange?.({
                          ...fullContent,
                          media: {
                            ...fullContent.media,
                            solutionVideos: { ...fullContent.media?.solutionVideos, [option.id]: url },
                          },
                        })
                      }
                    />
                  ) : null}
                  <ul className="solution-need__detail-bullets">
                    {option.bullets.map((b, bi) => (
                      <li key={bi} className={isEditing ? 'svcpg-removable' : ''}>
                        {T(`solutionNeed.options.${i}.bullets.${bi}`, `solutionNeed.options.${i}.bullets.${bi}`, b, 'span')}
                        <RemoveBtn path={`solutionNeed.options.${i}.bullets`} index={bi} small label="Remove bullet" />
                      </li>
                    ))}
                  </ul>
                  <AddBtn path={`solutionNeed.options.${i}.bullets`} label="Add bullet" template="New bullet" />
                  <div>
                    <Link to={option.ctaTo} className="solution-need__cta">
                      {T('solutionNeed.ctaLabel', 'solutionNeed.ctaLabel', data.solutionNeed.ctaLabel, 'span')}
                      {LinkPick(`solutionNeed.options.${i}.ctaTo`, option.ctaTo, <Icon name="arrowRight" size={13} />, 'Option link')}
                    </Link>
                  </div>
                </article>
              ))}
            </div>
            <AddBtn
              path="solutionNeed.options"
              label="Add option"
              template={{
                id: `option-${Date.now()}`,
                title: 'New option',
                shortLabel: 'New option',
                subheading: 'Option description',
                bullets: ['New bullet'],
                ctaTo: '/services',
                iconName: 'star',
              }}
            />
          </div>
        </section>
      ) : null}

      {/* ============ Numbers / stats ============ */}
      {showSection('numbersStats') ? (
        <section
          className="numbers-stats-section"
          ref={numbersStatsRef}
          data-svcpg-hidden={isEditing && isSectionHidden('numbersStats') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="numbersStats" label="Numbers / stats" />
          <div className="numbers-stats-section__wrap">
            <div className="numbers-stats-card">
              {data.numbersStats.items.map((item, i) => (
                <div key={i} className={`numbers-stat${isEditing ? ' svcpg-removable' : ''}`}>
                  {T(`numbersStats.items.${i}.value`, `numbersStats.items.${i}.value`, item.value, 'div', 'numbers-stat__value')}
                  {T(`numbersStats.items.${i}.label`, `numbersStats.items.${i}.label`, item.label, 'div', 'numbers-stat__label')}
                  <RemoveBtn path="numbersStats.items" index={i} label="Remove stat" />
                </div>
              ))}
            </div>
            <AddBtn path="numbersStats.items" label="Add stat" template={{ value: '0', label: 'NEW METRIC' }} />
          </div>
        </section>
      ) : null}

      {/* ============ AI Solutions ============ */}
      {showSection('aiSolutions') ? (
        <section
          className="ai-solutions-section"
          ref={aiSolutionsRef}
          data-svcpg-hidden={isEditing && isSectionHidden('aiSolutions') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="aiSolutions" label="AI Solutions" />
          <div className="section-container">
            <h2 className="section-headline">
              {T('aiSolutions.headingPrefix', 'aiSolutions.headingPrefix', data.aiSolutions.headingPrefix, 'span')}{' '}
              <span className="highlight">{T('aiSolutions.headingAccent', 'aiSolutions.headingAccent', data.aiSolutions.headingAccent, 'span')}</span>{' '}
              {T('aiSolutions.headingSuffix', 'aiSolutions.headingSuffix', data.aiSolutions.headingSuffix, 'span')}
            </h2>
            <p className="section-description">{T('aiSolutions.description', 'aiSolutions.description', data.aiSolutions.description, 'span')}</p>

            <div className="solutions-card">
              <div className="card-left">
                <div className="solutions-diagram-frame">
                  <EditableImage
                    blockId="aiSolutions.image"
                    editable={isEditing}
                    src={data.aiSolutions.imgSrc}
                    alt={data.aiSolutions.imgAlt}
                    onChange={({ src, alt }) =>
                      onChange?.({ data: setByPath(setByPath(data, 'aiSolutions.imgSrc', src), 'aiSolutions.imgAlt', alt) })
                    }
                    className="solutions-diagram"
                    placeholder={<Icon name="image" size={20} />}
                  />
                </div>
              </div>
              <div className="card-right">
                {T('aiSolutions.cardTitle', 'aiSolutions.cardTitle', data.aiSolutions.cardTitle, 'h3', 'card-title')}
                {T('aiSolutions.cardDescription', 'aiSolutions.cardDescription', data.aiSolutions.cardDescription, 'p', 'card-description')}
                {T('aiSolutions.capabilitiesLabel', 'aiSolutions.capabilitiesLabel', data.aiSolutions.capabilitiesLabel, 'div', 'capabilities-heading')}
                <ul className="capabilities-list">
                  {data.aiSolutions.capabilities.map((item, i) => (
                    <li key={i} className={isEditing ? 'svcpg-removable' : ''}>
                      {T(`aiSolutions.capabilities.${i}`, `aiSolutions.capabilities.${i}`, item, 'span')}
                      <RemoveBtn path="aiSolutions.capabilities" index={i} small label="Remove capability" />
                    </li>
                  ))}
                </ul>
                <AddBtn path="aiSolutions.capabilities" label="Add capability" template="New capability" />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ AI Automation ============ */}
      {showSection('aiAutomation') ? (
        <section
          className="ai-automation-section"
          ref={aiAutomationRef}
          data-svcpg-hidden={isEditing && isSectionHidden('aiAutomation') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="aiAutomation" label="AI Automation" />
          <div className="section-container">
            <div className="automation-card">
              <div className="automation-left">
                {T('aiAutomation.title', 'aiAutomation.title', data.aiAutomation.title, 'h3', 'automation-title')}
                {T('aiAutomation.description', 'aiAutomation.description', data.aiAutomation.description, 'p', 'automation-description')}
                {T('aiAutomation.capabilitiesLabel', 'aiAutomation.capabilitiesLabel', data.aiAutomation.capabilitiesLabel, 'div', 'capabilities-heading')}
                <ul className="capabilities-list">
                  {data.aiAutomation.capabilities.map((item, i) => (
                    <li key={i} className={isEditing ? 'svcpg-removable' : ''}>
                      {T(`aiAutomation.capabilities.${i}`, `aiAutomation.capabilities.${i}`, item, 'span')}
                      <RemoveBtn path="aiAutomation.capabilities" index={i} small label="Remove capability" />
                    </li>
                  ))}
                </ul>
                <AddBtn path="aiAutomation.capabilities" label="Add capability" template="New capability" />
              </div>
              <div className="automation-right">
                <div className="automation-gif-frame">
                  <EditableImage
                    blockId="aiAutomation.image"
                    editable={isEditing}
                    src={data.aiAutomation.imgSrc}
                    alt={data.aiAutomation.imgAlt}
                    onChange={({ src, alt }) =>
                      onChange?.({ data: setByPath(setByPath(data, 'aiAutomation.imgSrc', src), 'aiAutomation.imgAlt', alt) })
                    }
                    className="automation-gif"
                    placeholder={<Icon name="image" size={20} />}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ Case studies (headline/description only — cards live on the Case Studies tab) ============ */}
      {showSection('homeCaseStudies') ? (
        <section
          className="case-studies-section"
          ref={caseStudiesRef}
          data-svcpg-hidden={isEditing && isSectionHidden('homeCaseStudies') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="homeCaseStudies" label="Case studies" />
          <div className="section-container">
            <h2 className="section-headline">
              {T('homeCaseStudies.headlinePrefix', 'homeCaseStudies.headlinePrefix', data.homeCaseStudies.headlinePrefix, 'span')}{' '}
              <span className="headline-purple">
                {T('homeCaseStudies.headlineAccent', 'homeCaseStudies.headlineAccent', data.homeCaseStudies.headlineAccent, 'span')}
              </span>{' '}
              {T('homeCaseStudies.headlineSuffix', 'homeCaseStudies.headlineSuffix', data.homeCaseStudies.headlineSuffix, 'span')}
            </h2>
            <p className="section-description">
              {T('homeCaseStudies.description', 'homeCaseStudies.description', data.homeCaseStudies.description, 'span')}
            </p>
            {isEditing && fullContent ? (
              <div className="svcpg-embedded-editor">
                <CaseStudiesEditor content={fullContent} onChange={onFullContentChange} />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ============ Why AI ============ */}
      {showSection('whyAI') ? (
        <section
          className="why-ai-section"
          ref={whyAIRef}
          data-svcpg-hidden={isEditing && isSectionHidden('whyAI') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="whyAI" label="Why AI" />
          <div className="why-ai-container">
            <h2 className="why-ai-heading">
              {T('whyAI.headingLine1Prefix', 'whyAI.headingLine1Prefix', data.whyAI.headingLine1Prefix, 'span')}{' '}
              <span className="why-ai-purple">{T('whyAI.headingLine1Accent', 'whyAI.headingLine1Accent', data.whyAI.headingLine1Accent, 'span')}</span>{' '}
              {T('whyAI.headingLine1Suffix', 'whyAI.headingLine1Suffix', data.whyAI.headingLine1Suffix, 'span')}
              <br />
              {T('whyAI.headingLine2Prefix', 'whyAI.headingLine2Prefix', data.whyAI.headingLine2Prefix, 'span')}{' '}
              <span className="why-ai-purple">{T('whyAI.headingLine2Accent', 'whyAI.headingLine2Accent', data.whyAI.headingLine2Accent, 'span')}</span>{' '}
              {T('whyAI.headingLine2Suffix', 'whyAI.headingLine2Suffix', data.whyAI.headingLine2Suffix, 'span')}
            </h2>
            <p className="why-ai-description">{T('whyAI.description', 'whyAI.description', data.whyAI.description, 'span')}</p>

            <div className="why-ai-grid">
              <div className="why-ai-left">
                <div className="why-ai-complaints-heading">
                  <span className="why-ai-q-icon">?</span>
                  {T('whyAI.complaintsLabel', 'whyAI.complaintsLabel', data.whyAI.complaintsLabel, 'span')}
                </div>
                <div className="why-ai-complaints">
                  {data.whyAI.complaints.map((c, i) => (
                    <div key={i} className={`why-ai-complaint-card${isEditing ? ' svcpg-removable' : ''}`}>
                      {T(`whyAI.complaints.${i}.quote`, `whyAI.complaints.${i}.quote`, c.quote, 'p', 'complaint-quote')}
                      {T(`whyAI.complaints.${i}.sub`, `whyAI.complaints.${i}.sub`, c.sub, 'p', 'complaint-sub')}
                      <RemoveBtn path="whyAI.complaints" index={i} label="Remove complaint" />
                    </div>
                  ))}
                </div>
                <AddBtn path="whyAI.complaints" label="Add complaint" template={{ quote: 'New complaint', sub: 'New sub-text' }} />
              </div>

              <div className="why-ai-right">
                <h3 className="diff-heading">
                  {T('whyAI.rightHeadingLine1', 'whyAI.rightHeadingLine1', data.whyAI.rightHeadingLine1, 'span')}
                  <br />
                  <span className="diff-heading-purple">
                    {T('whyAI.rightHeadingLine2', 'whyAI.rightHeadingLine2', data.whyAI.rightHeadingLine2, 'span')}
                  </span>
                </h3>
                <p className="diff-description">{T('whyAI.rightDescription', 'whyAI.rightDescription', data.whyAI.rightDescription, 'span')}</p>
                <ul className="diff-list">
                  {data.whyAI.differentiators.map((d, i) => (
                    <li key={i} className={`diff-list-item${isEditing ? ' svcpg-removable' : ''}`}>
                      <span className="diff-check">✔</span>
                      {T(`whyAI.differentiators.${i}`, `whyAI.differentiators.${i}`, d, 'span')}
                      <RemoveBtn path="whyAI.differentiators" index={i} small label="Remove item" />
                    </li>
                  ))}
                </ul>
                <AddBtn path="whyAI.differentiators" label="Add item" template="New differentiator" />
              </div>
            </div>
          </div>
        </section>
      ) : null}

      {/* ============ How Databiqs ============ */}
      {showSection('howDatabiqs') ? (
        <section
          className="how-databiqs-section"
          ref={howDatabiqsRef}
          data-svcpg-hidden={isEditing && isSectionHidden('howDatabiqs') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="howDatabiqs" label="How Databiqs" />
          <div className="how-databiqs-container">
            <h2 className="how-databiqs-heading">
              {T('howDatabiqs.headingPrefix', 'howDatabiqs.headingPrefix', data.howDatabiqs.headingPrefix, 'span')}{' '}
              <span className="how-databiqs-purple">
                {T('howDatabiqs.headingAccent', 'howDatabiqs.headingAccent', data.howDatabiqs.headingAccent, 'span')}
              </span>{' '}
              {T('howDatabiqs.headingSuffix', 'howDatabiqs.headingSuffix', data.howDatabiqs.headingSuffix, 'span')}
            </h2>
            <p className="how-databiqs-description">
              {T('howDatabiqs.description', 'howDatabiqs.description', data.howDatabiqs.description, 'span')}
            </p>

            <div className="how-databiqs-grid">
              {data.howDatabiqs.cards.map((card, i) => (
                <div key={i} className={`how-databiqs-card${isEditing ? ' svcpg-removable' : ''}`}>
                  <div className="hd-icon-box">
                    {IconPick(`howDatabiqs.cards.${i}.icon`, `howDatabiqs.cards.${i}.iconName`, card.iconName, 22)}
                  </div>
                  {T(`howDatabiqs.cards.${i}.title`, `howDatabiqs.cards.${i}.title`, card.title, 'h3', 'hd-card-title')}
                  {T(`howDatabiqs.cards.${i}.description`, `howDatabiqs.cards.${i}.description`, card.description, 'p', 'hd-card-desc')}
                  <RemoveBtn path="howDatabiqs.cards" index={i} label="Remove card" />
                </div>
              ))}
            </div>
            <AddBtn
              path="howDatabiqs.cards"
              label="Add card"
              template={{ iconName: 'star', title: 'New step', description: 'Card description' }}
            />
          </div>
        </section>
      ) : null}

      {/* ============ Testimonials (heading/subheading only — items live on the Testimonials tab) ============ */}
      {showSection('testimonials') ? (
        <section
          className="testimonials-section"
          ref={testimonialsRef}
          data-svcpg-hidden={isEditing && isSectionHidden('testimonials') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="testimonials" label="Testimonials" />
          <div className="section-container">
            <h2 className="section-headline">
              {T('testimonialsHeading.headingPrefix', 'testimonialsHeading.headingPrefix', data.testimonialsHeading.headingPrefix, 'span')}{' '}
              <span className="headline-purple">
                {T('testimonialsHeading.headingAccent', 'testimonialsHeading.headingAccent', data.testimonialsHeading.headingAccent, 'span')}
              </span>{' '}
              {T('testimonialsHeading.headingSuffix', 'testimonialsHeading.headingSuffix', data.testimonialsHeading.headingSuffix, 'span')}
            </h2>
            <p className="section-description">
              {T('testimonialsHeading.description', 'testimonialsHeading.description', data.testimonialsHeading.description, 'span')}
            </p>
            {isEditing && fullContent ? (
              <div className="svcpg-embedded-editor">
                <TestimonialsEditor content={fullContent} onChange={onFullContentChange} />
              </div>
            ) : null}
          </div>
        </section>
      ) : null}

      {/* ============ Final CTA (inline rebuild of MeetingBannerSection so it's editable) ============ */}
      {showSection('finalCta') ? (
        <section
          className="meeting-banner-section"
          ref={finalCtaRef}
          data-svcpg-hidden={isEditing && isSectionHidden('finalCta') ? 'true' : undefined}
        >
          <SectionToggle sectionKey="finalCta" label="Final CTA" />
          <div className="section-container meeting-banner-section__wrap">
            <div className="meeting-banner">
              <div className="meeting-banner__decor meeting-banner__decor--bl" aria-hidden />
              <div className="meeting-banner__decor meeting-banner__decor--tr" aria-hidden />
              <div className="meeting-banner__inner">
                <h2 className="meeting-banner__title">{T('finalCta.title', 'finalCta.title', data.finalCta.title, 'span')}</h2>
                <p className="meeting-banner__sub">{T('finalCta.subtitle', 'finalCta.subtitle', data.finalCta.subtitle, 'span')}</p>
                <Link to={data.finalCta.ctaTo} className="meeting-banner__cta">
                  {T('finalCta.ctaLabel', 'finalCta.ctaLabel', data.finalCta.ctaLabel, 'span', 'meeting-banner__cta-text')}
                  <span className="meeting-banner__cta-arrow" aria-hidden>
                    {LinkPick('finalCta.ctaTo', data.finalCta.ctaTo, <img src={arrowIcon} alt="" />, 'Final CTA link')}
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </section>
      ) : null}

      <Footer />
    </div>
  );
}
